import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, Search, ChefHat, Trash2, Pencil, Eye, BookOpen, Loader2, Copy, TrendingUp, ChevronDown, ChevronUp, Scale } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import PricingPanel from './recipes/PricingPanel';
import ComparisonPanel from './recipes/ComparisonPanel';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '@/components/ui/alert-dialog';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { toast } from 'sonner';
import { recipesAPI } from '@/lib/api';
import { formatCurrency, pluralUnit } from './recipes/recipeUtils';
import RecipeFormDialog from './recipes/RecipeFormDialog';
import RecipeDetailDialog from './recipes/RecipeDetailDialog';
import { useAuth } from '@/context/AuthContext';

export default function RecipesPage() {
    const { hasPermission, user } = useAuth();
    const isOperator = user?.role === 'operator' || (user?.role === 'production' && (user?.allowed_sites || []).length > 0);
    const canViewPrices = hasPermission('prices_view') && user?.role !== 'operator';
    const canCreateRecipe = hasPermission('recipes_create');
    const canEditRecipe = hasPermission('recipes_edit');
    const canDeleteRecipe = hasPermission('recipes_delete');
    const [recipes, setRecipes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [categories, setCategories] = useState([]);
    const [typeFilter, setTypeFilter] = useState('recipes');
    const [companyFilters, setCompanyFilters] = useState([]);

    const [showForm, setShowForm] = useState(false);
    const [editingRecipe, setEditingRecipe] = useState(null);
    const [showDetail, setShowDetail] = useState(false);
    const [selectedRecipe, setSelectedRecipe] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [showPricing, setShowPricing] = useState(false);
    const [showComparison, setShowComparison] = useState(false);

    const fetchRecipes = useCallback(async () => {
        try {
            setLoading(true);
            const params = {};
            if (categoryFilter && categoryFilter !== 'all') params.category = categoryFilter;
            if (search) params.search = search;
            if (typeFilter === 'recipes') params.is_sub_recipe = 'false';
            else if (typeFilter === 'sub_recipes') params.is_sub_recipe = 'true';
            const res = await recipesAPI.getAll(params);
            setRecipes(res.data);
        } catch (err) {
            toast.error('Erreur lors du chargement des recettes');
        } finally {
            setLoading(false);
        }
    }, [categoryFilter, search, typeFilter]);

    const fetchCategories = useCallback(async () => {
        try {
            const res = await recipesAPI.getCategories();
            setCategories(res.data);
        } catch { /* ignore */ }
    }, []);

    useEffect(() => { fetchRecipes(); }, [fetchRecipes]);
    useEffect(() => { fetchCategories(); }, [fetchCategories]);

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await recipesAPI.delete(deleteTarget.id);
            toast.success('Recette supprimée');
            setDeleteTarget(null);
            fetchRecipes();
            fetchCategories();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors de la suppression');
        } finally {
            setDeleting(false);
        }
    };

    const openEdit = (recipe) => {
        setEditingRecipe(recipe);
        setShowForm(true);
    };

    const openDetail = (recipe) => {
        setSelectedRecipe(recipe);
        setShowDetail(true);
    };

    const duplicateRecipe = async (recipe) => {
        try {
            const res = await recipesAPI.getById(recipe.id);
            const src = res.data;
            const copy = {
                ...src,
                id: undefined,
                name: `${src.name} (Copie)`,
                ingredients: (src.ingredients || []).map(ing => ({
                    type: ing.type,
                    industrial_product_id: ing.industrial_product_id || null,
                    supplier_product_id: ing.supplier_product_id || null,
                    sub_recipe_id: ing.sub_recipe_id || null,
                    quantity: ing.quantity,
                    unit: ing.unit,
                    use_base_unit: ing.use_base_unit || false,
                    contenance: ing.contenance || null,
                    industrial_product_name: ing.industrial_product_name || '',
                    supplier_product_name: ing.supplier_product_name || '',
                    sub_recipe_name: ing.sub_recipe_name || '',
                    base_unit: ing.base_unit || '',
                    purchase_unit: ing.purchase_unit || '',
                    unit_cost: ing.unit_cost || 0,
                    total_cost: ing.total_cost || 0,
                    _raw_unit_price: ing.purchase_unit_price || ing.unit_cost || 0,
                    _supplierProducts: [],
                })),
            };
            setEditingRecipe(copy);
            setShowForm(true);
            toast.info('Recette dupliquée — modifiez le nom et enregistrez');
        } catch {
            toast.error('Erreur lors de la duplication');
        }
    };

    const filteredRecipes = useMemo(() => {
        if (companyFilters.length === 0) return recipes;
        return recipes.filter(r => companyFilters.some(key => r[key] !== false));
    }, [recipes, companyFilters]);

    const groupedRecipes = useMemo(() => {
        const groups = {};
        filteredRecipes.forEach(r => {
            const cat = r.category || 'Sans catégorie';
            if (!groups[cat]) groups[cat] = [];
            groups[cat].push(r);
        });
        return Object.entries(groups).sort(([a], [b]) => {
            if (a === 'Sans catégorie') return 1;
            if (b === 'Sans catégorie') return -1;
            return a.localeCompare(b);
        });
    }, [filteredRecipes]);

    return (
        <div className="space-y-3" data-testid="recipes-page">
            {/* Header + Actions (compact) */}
            <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-lg font-bold text-slate-900 tracking-tight mr-2" data-testid="recipes-title">Recettes</h1>
                <div className="flex gap-1 bg-slate-100 rounded-lg p-0.5 w-fit">
                    {[
                        { value: 'recipes', label: 'Recettes' },
                        { value: 'sub_recipes', label: 'Sous-recettes' },
                        { value: 'all', label: 'Tout' },
                    ].map(tab => (
                        <button
                            key={tab.value}
                            onClick={() => setTypeFilter(tab.value)}
                            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                                typeFilter === tab.value
                                    ? 'bg-white text-slate-900 shadow-sm'
                                    : 'text-slate-500 hover:text-slate-700'
                            }`}
                            data-testid={`tab-${tab.value}`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
                <div className="flex gap-2 ml-auto">
                    {canViewPrices && (
                        <Button
                            size="sm"
                            variant={showPricing ? "default" : "outline"}
                            onClick={() => setShowPricing(v => !v)}
                            className={`h-8 text-xs ${showPricing ? "bg-emerald-600 text-white hover:bg-emerald-700" : ""}`}
                            data-testid="toggle-pricing-btn"
                        >
                            <TrendingUp className="w-3.5 h-3.5 mr-1.5" />
                            Tarifs & Marges
                            {showPricing ? <ChevronUp className="w-3.5 h-3.5 ml-1" /> : <ChevronDown className="w-3.5 h-3.5 ml-1" />}
                        </Button>
                    )}
                    {canViewPrices && (
                        <Button
                            size="sm"
                            variant={showComparison ? "default" : "outline"}
                            onClick={() => setShowComparison(v => !v)}
                            className={`h-8 text-xs ${showComparison ? "bg-violet-600 text-white hover:bg-violet-700" : ""}`}
                            data-testid="toggle-comparison-btn"
                        >
                            <Scale className="w-3.5 h-3.5 mr-1.5" />
                            Comparatif clients
                            {showComparison ? <ChevronUp className="w-3.5 h-3.5 ml-1" /> : <ChevronDown className="w-3.5 h-3.5 ml-1" />}
                        </Button>
                    )}
                    {canCreateRecipe && (
                    <Button
                        size="sm"
                        onClick={() => { setEditingRecipe(null); setShowForm(true); }}
                        className="h-8 text-xs bg-slate-900 text-white hover:bg-slate-800"
                        data-testid="create-recipe-btn"
                    >
                        <Plus className="w-3.5 h-3.5 mr-1.5" /> Nouvelle recette
                    </Button>
                    )}
                </div>
            </div>

            {canViewPrices && showPricing && (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5" data-testid="pricing-section">
                    <PricingPanel />
                </div>
            )}

            {canViewPrices && showComparison && (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5" data-testid="comparison-section">
                    <ComparisonPanel />
                </div>
            )}

            {/* Filters (compact) */}
            <div className="flex flex-col sm:flex-row gap-2">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                    <Input
                        className="pl-8 h-8 text-sm"
                        placeholder="Rechercher une recette..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        data-testid="recipes-search-input"
                    />
                </div>
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                    <SelectTrigger className="w-48 h-8 text-sm" data-testid="recipes-category-filter">
                        <SelectValue placeholder="Toutes les catégories" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Toutes les catégories</SelectItem>
                        {categories.map((c) => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {!isOperator && (
                <div className="flex gap-1.5 items-center flex-wrap" data-testid="company-filter-chips">
                    <button
                        type="button"
                        onClick={() => setCompanyFilters([])}
                        className={`px-3 h-7 rounded-full text-xs font-semibold transition-colors border ${
                            companyFilters.length === 0
                                ? 'bg-slate-900 text-white border-slate-900'
                                : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                        }`}
                        data-testid="company-filter-all"
                    >
                        Tous
                    </button>
                    {[
                        { value: 'my_food', label: 'My Food' },
                        { value: 'fuji_express', label: 'Fuji Express' },
                        { value: 'concept_lab', label: 'Concept Lab' },
                        { value: 'boucherie', label: 'Boucherie' },
                    ].map(c => {
                        const active = companyFilters.includes(c.value);
                        return (
                            <button
                                key={c.value}
                                type="button"
                                onClick={() => setCompanyFilters(prev =>
                                    active ? prev.filter(v => v !== c.value) : [...prev, c.value]
                                )}
                                className={`px-3 h-7 rounded-full text-xs font-semibold transition-colors border ${
                                    active
                                        ? 'bg-slate-900 text-white border-slate-900'
                                        : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                                }`}
                                data-testid={`company-filter-${c.value}`}
                            >
                                {c.label}
                            </button>
                        );
                    })}
                </div>
                )}
            </div>

            {/* Recipes List */}
            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                </div>
            ) : recipes.length === 0 ? (
                <div className="text-center py-20 bg-white rounded-xl border border-slate-200">
                    <ChefHat className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <h3 className="text-lg font-semibold text-slate-700">Aucune recette</h3>
                    <p className="text-sm text-slate-400 mt-1">
                        {search || categoryFilter !== 'all' ? 'Aucun résultat pour cette recherche' : 'Créez votre première recette pour commencer'}
                    </p>
                </div>
            ) : (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-slate-50">
                                <TableHead className="text-xs uppercase tracking-wider font-medium text-slate-500">Nom</TableHead>
                                <TableHead className="text-xs uppercase tracking-wider font-medium text-slate-500">Catégorie</TableHead>
                                <TableHead className="text-xs uppercase tracking-wider font-medium text-slate-500 text-center">Ingrédients</TableHead>
                                <TableHead className="text-xs uppercase tracking-wider font-medium text-slate-500 text-center">Rendement</TableHead>
                                {canViewPrices && <TableHead className="text-xs uppercase tracking-wider font-medium text-slate-500 text-right">Coût total</TableHead>}
                                {canViewPrices && <TableHead className="text-xs uppercase tracking-wider font-medium text-slate-500 text-right">Coût unitaire</TableHead>}
                                <TableHead className="text-xs uppercase tracking-wider font-medium text-slate-500 text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {groupedRecipes.map(([category, catRecipes]) => (
                                <React.Fragment key={category}>
                                    <TableRow className="bg-slate-50/80 border-t border-slate-200">
                                        <TableCell colSpan={canViewPrices ? 7 : 5} className="py-1.5 px-4">
                                            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500" data-testid={`category-header-${category}`}>
                                                {category}
                                            </span>
                                            <span className="text-[10px] text-slate-400 ml-2">({catRecipes.length})</span>
                                        </TableCell>
                                    </TableRow>
                                    {catRecipes.map((recipe) => (
                                <TableRow
                                    key={recipe.id}
                                    className="hover:bg-slate-50/50 transition-colors cursor-pointer"
                                    onClick={() => openDetail(recipe)}
                                    data-testid={`recipe-row-${recipe.id}`}
                                >
                                    <TableCell className="py-1">
                                        <div className="flex items-center gap-3">
                                            {recipe.image_url ? (
                                                <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 border border-slate-200">
                                                    <img src={`${process.env.REACT_APP_BACKEND_URL}${recipe.image_url}`} alt="" className="w-full h-full object-cover" />
                                                </div>
                                            ) : (
                                                <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${recipe.is_sub_recipe ? 'bg-violet-50' : 'bg-orange-50'}`}>
                                                    {recipe.is_sub_recipe
                                                        ? <BookOpen className="w-5 h-5 text-violet-500" />
                                                        : <ChefHat className="w-5 h-5 text-orange-500" />}
                                                </div>
                                            )}
                                            <div>
                                                <div className="flex items-center gap-1.5">
                                                    <p className="font-medium text-sm text-slate-900">{recipe.name}</p>
                                                    {recipe.is_sub_recipe && (
                                                        <span className="text-[10px] bg-violet-100 text-violet-600 px-1.5 py-0.5 rounded-full font-medium">sous-recette</span>
                                                    )}
                                                </div>
                                                {recipe.description && (
                                                    <p className="text-xs text-slate-400 truncate max-w-48">{recipe.description}</p>
                                                )}
                                                {recipe.allergens && recipe.allergens.length > 0 && (
                                                    <div className="flex items-center gap-0.5 flex-wrap mt-0.5">
                                                        {recipe.allergens.map((a) => (
                                                            <span key={a} className="px-1 py-0 bg-orange-100 text-orange-600 text-[9px] font-medium rounded">
                                                                {a}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        {recipe.category ? (
                                            <span className="inline-block bg-slate-100 text-slate-600 text-xs px-2 py-0.5 rounded-full">
                                                {recipe.category}
                                            </span>
                                        ) : (
                                            <span className="text-xs text-slate-300">-</span>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-center text-sm text-slate-600">
                                        {recipe.ingredients?.length || 0}
                                    </TableCell>
                                    <TableCell className="text-center text-sm text-slate-600">
                                        {recipe.portions} {pluralUnit(recipe.yield_unit || 'portion', recipe.portions)}
                                    </TableCell>
                                    {canViewPrices && (
                                        <TableCell className="text-right text-sm font-medium text-slate-700">
                                            {formatCurrency(recipe.total_cost)}
                                        </TableCell>
                                    )}
                                    {canViewPrices && (
                                        <TableCell className="text-right">
                                            <span className="inline-block bg-orange-50 text-orange-600 font-semibold text-sm px-2 py-0.5 rounded" data-testid={`recipe-cpp-${recipe.id}`}>
                                                {formatCurrency(recipe.cost_per_portion)}/{pluralUnit(recipe.yield_unit || 'portion', 1)}
                                            </span>
                                        </TableCell>
                                    )}
                                    <TableCell className="text-right">
                                        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                                            <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-slate-600" onClick={() => openDetail(recipe)} data-testid={`recipe-view-${recipe.id}`}>
                                                <Eye className="w-4 h-4" />
                                            </Button>
                                            {canEditRecipe && (
                                            <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-blue-600" onClick={() => openEdit(recipe)} data-testid={`recipe-edit-${recipe.id}`}>
                                                <Pencil className="w-4 h-4" />
                                            </Button>
                                            )}
                                            {canCreateRecipe && (
                                            <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-emerald-600" onClick={() => duplicateRecipe(recipe)} data-testid={`recipe-duplicate-${recipe.id}`} title="Dupliquer">
                                                <Copy className="w-4 h-4" />
                                            </Button>
                                            )}
                                            {canDeleteRecipe && (
                                            <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-red-500" onClick={() => setDeleteTarget(recipe)} data-testid={`recipe-delete-${recipe.id}`}>
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                            )}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                                </React.Fragment>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}

            <RecipeFormDialog
                open={showForm}
                onClose={() => setShowForm(false)}
                recipe={editingRecipe}
                onSaved={() => { fetchRecipes(); fetchCategories(); }}
            />

            <RecipeDetailDialog
                open={showDetail}
                onClose={() => setShowDetail(false)}
                recipe={selectedRecipe}
            />

            <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
                <AlertDialogContent data-testid="delete-recipe-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer la recette</AlertDialogTitle>
                        <AlertDialogDescription>
                            Êtes-vous sûr de vouloir supprimer la recette "{deleteTarget?.name}" ? Cette action est irréversible.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="delete-recipe-cancel">Annuler</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDelete}
                            className="bg-red-600 hover:bg-red-700"
                            disabled={deleting}
                            data-testid="delete-recipe-confirm"
                        >
                            {deleting ? 'Suppression...' : 'Supprimer'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
